/**
 * Contact Filter Popover
 * Popover for filtering contacts by primary status and active status
 * Following the same pattern as vendor FilterPopover
 */

import { useState, useEffect } from 'react';
import { Popover, Button, Radio, Space, Divider, Typography, theme } from 'antd';
import type { RadioChangeEvent } from 'antd';
import { useTranslation } from 'react-i18next';
import { controlHeightTokens, radiusTokens } from '@/styles/tokens';

const { Text } = Typography;

interface ContactFilterPopoverProps {
  children: React.ReactNode;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onApply: (filters: { isPrimary: boolean | null; isActive: boolean | null }) => void;
  initialFilters: {
    isPrimary: boolean | null;
    isActive: boolean | null;
  };
}

const ContactFilterPopover = ({
  children,
  open,
  onOpenChange,
  onApply,
  initialFilters,
}: ContactFilterPopoverProps) => {
  const { token } = theme.useToken();
  const { t: tContacts } = useTranslation('contacts');

  // Local state for filter values - using radio button approach
  // Primary filter: 'primary' | 'general' | 'all'
  const [primaryFilter, setPrimaryFilter] = useState<'primary' | 'general' | 'all'>(
    initialFilters.isPrimary === true
      ? 'primary'
      : initialFilters.isPrimary === false
        ? 'general'
        : 'all'
  );
  // Status filter: 'active' | 'inactive' | 'all'
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive' | 'all'>(
    initialFilters.isActive === true
      ? 'active'
      : initialFilters.isActive === false
        ? 'inactive'
        : 'all'
  );

  // Sync with initial values when popover opens
  useEffect(() => {
    if (open) {
      setPrimaryFilter(
        initialFilters.isPrimary === true
          ? 'primary'
          : initialFilters.isPrimary === false
            ? 'general'
            : 'all'
      );
      setStatusFilter(
        initialFilters.isActive === true
          ? 'active'
          : initialFilters.isActive === false
            ? 'inactive'
            : 'all'
      );
    }
  }, [open, initialFilters.isPrimary, initialFilters.isActive]);

  const handleResetPrimary = () => {
    setPrimaryFilter('all');
  };

  const handleResetStatus = () => {
    setStatusFilter('all');
  };

  const handleApply = () => {
    // Convert radio to isPrimary value
    let isPrimary: boolean | null = null;
    if (primaryFilter === 'primary') {
      isPrimary = true;
    } else if (primaryFilter === 'general') {
      isPrimary = false;
    }
    // 'all' means no filter (null)

    // Convert radio to isActive value
    let isActive: boolean | null = null;
    if (statusFilter === 'active') {
      isActive = true;
    } else if (statusFilter === 'inactive') {
      isActive = false;
    }
    // 'all' means no filter (null)

    onApply({ isPrimary, isActive });
    onOpenChange(false);
  };

  const handleCancel = () => {
    // Reset to initial values
    setPrimaryFilter(
      initialFilters.isPrimary === true
        ? 'primary'
        : initialFilters.isPrimary === false
          ? 'general'
          : 'all'
    );
    setStatusFilter(
      initialFilters.isActive === true
        ? 'active'
        : initialFilters.isActive === false
          ? 'inactive'
          : 'all'
    );
    onOpenChange(false);
  };

  const content = (
    <div style={{ padding: '12px' }}>
      <Space direction="vertical" size={12} style={{ width: '100%' }}>
        {/* Primary Contact Filter Section - Radio Button Cards */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {tContacts('filter.contact')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetPrimary}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {tContacts('filter.reset')}
            </Button>
          </div>
          <style>
            {`
              .primary-card-filter {
                flex: 1;
                padding: 10px 12px;
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .primary-card-filter:hover {
                background-color: ${token.colorPrimaryBg} !important;
                border-color: ${token.colorPrimary} !important;
                transform: translateY(-1px);
              }

              .primary-card-filter .ant-radio-wrapper {
                margin-right: 0;
              }
            `}
          </style>
          <Radio.Group
            value={primaryFilter}
            onChange={(e: RadioChangeEvent) => setPrimaryFilter(e.target.value)}
            style={{ width: '100%' }}
          >
            <div className="tw-flex tw-gap-2">
              {(
                [
                  { value: 'primary', label: tContacts('filter.primary') },
                  { value: 'general', label: tContacts('filter.general') },
                  { value: 'all', label: tContacts('filter.all') },
                ] as const
              ).map((option) => (
                <div
                  key={option.value}
                  className="primary-card-filter"
                  style={{
                    backgroundColor:
                      primaryFilter === option.value
                        ? token.colorPrimaryBg
                        : token.colorBgContainer,
                    borderRadius: radiusTokens.card,
                    border: `1.5px solid ${
                      primaryFilter === option.value ? token.colorPrimary : token.colorBorder
                    }`,
                  }}
                  onClick={() => setPrimaryFilter(option.value)}
                >
                  <Radio value={option.value}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {option.label}
                    </Text>
                  </Radio>
                </div>
              ))}
            </div>
          </Radio.Group>
        </div>

        {/* Data Status Filter Section - Radio Button Cards */}
        <div
          style={{
            padding: '12px',
            backgroundColor: token.colorFillQuaternary,
            borderRadius: radiusTokens.panel,
          }}
        >
          <div className="tw-flex tw-items-center tw-justify-between tw-mb-2">
            <Text
              strong
              style={{
                fontSize: token.fontSizeSM,
                color: token.colorText,
              }}
            >
              {tContacts('filter.status')}
            </Text>
            <Button
              type="link"
              size="small"
              onClick={handleResetStatus}
              style={{ padding: 0, height: 'auto', fontSize: '12px' }}
            >
              {tContacts('filter.reset')}
            </Button>
          </div>
          <style>
            {`
              .status-card-filter {
                flex: 1;
                padding: 10px 12px;
                cursor: pointer;
                transition: all 0.25s cubic-bezier(0.4, 0, 0.2, 1);
                display: flex;
                align-items: center;
                justify-content: center;
              }

              .status-card-filter:hover {
                background-color: ${token.colorPrimaryBg} !important;
                border-color: ${token.colorPrimary} !important;
                transform: translateY(-1px);
              }

              .status-card-filter .ant-radio-wrapper {
                margin-right: 0;
              }
            `}
          </style>
          <Radio.Group
            value={statusFilter}
            onChange={(e: RadioChangeEvent) => setStatusFilter(e.target.value)}
            style={{ width: '100%' }}
          >
            <div className="tw-flex tw-gap-2">
              {(
                [
                  { value: 'active', label: tContacts('filter.active') },
                  { value: 'inactive', label: tContacts('filter.inactive') },
                  { value: 'all', label: tContacts('filter.all') },
                ] as const
              ).map((option) => (
                <div
                  key={option.value}
                  className="status-card-filter"
                  style={{
                    backgroundColor:
                      statusFilter === option.value ? token.colorPrimaryBg : token.colorBgContainer,
                    borderRadius: radiusTokens.card,
                    border: `1.5px solid ${
                      statusFilter === option.value ? token.colorPrimary : token.colorBorder
                    }`,
                  }}
                  onClick={() => setStatusFilter(option.value)}
                >
                  <Radio value={option.value}>
                    <Text strong style={{ fontSize: '13px' }}>
                      {option.label}
                    </Text>
                  </Radio>
                </div>
              ))}
            </div>
          </Radio.Group>
        </div>

        <Divider style={{ margin: 0 }} />

        {/* Action Buttons */}
        <div className="tw-flex tw-gap-2 tw-justify-end">
          <Button
            onClick={handleCancel}
            style={{
              height: `${controlHeightTokens.controlHeightLG}px`,
              fontSize: token.fontSizeSM,
            }}
          >
            {tContacts('filter.cancel')}
          </Button>
          <Button
            type="primary"
            onClick={handleApply}
            style={{
              height: `${controlHeightTokens.controlHeightLG}px`,
              fontSize: token.fontSizeSM,
            }}
          >
            {tContacts('filter.apply')}
          </Button>
        </div>
      </Space>
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={onOpenChange}
      placement="bottomLeft"
      arrow={false}
      overlayStyle={{
        width: '396px',
      }}
      styles={{
        body: {
          padding: 0,
          backgroundColor: token.colorBgElevated,
          border: `1px solid ${token.colorBorderSecondary}`,
          borderRadius: radiusTokens.panel,
          boxShadow:
            '0 12px 28px 0 rgba(0, 0, 0, 0.12), 0 6px 12px -6px rgba(0, 0, 0, 0.16), 0 18px 56px 16px rgba(0, 0, 0, 0.08)',
        },
      }}
    >
      {children}
    </Popover>
  );
};

export default ContactFilterPopover;
